import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, effect, inject } from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormField, MatLabel, MatPrefix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { AbTestStore } from "../../../stores/ab-tests/ab-test.store";
import { DateTime } from "luxon";
import { AbTestGroupViewModel } from "@playcharge/common/models/backend/ab-tests/ab-test-group-view-model";
import { MatButton } from "@angular/material/button";
import { AbTestGroupForm } from "../../../models/ab-tests/ab-test-group-form";
import { AbTestsStore } from "../../../stores/ab-tests/ab-tests.store";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { AbTestEditModel } from "@playcharge/common/models/backend/ab-tests/ab-test-edit-model";
import { Observable } from "rxjs";
import { AbTestViewModel } from "@playcharge/common/models/backend/ab-tests/ab-test-view-model";
import { ToastrService } from "ngx-toastr";
import { MatOption } from "@angular/material/autocomplete";
import { OffersStore } from "../../../stores/offers/offers.store";
import { MatSelect } from "@angular/material/select";
import { NoScrollDirective } from "../../../directives/noScrollDirective";

@Component({
    selector: "app-ab-test",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatPrefix,
        MatSelect,
        MatOption,
        MatCheckbox,
        MatButton,
        NoScrollDirective,
    ],
    templateUrl: "./ab-test.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [AbTestStore],
})
export class AbTestComponent {
    private abTestStore = inject(AbTestStore);
    private abTestsStore = inject(AbTestsStore);
    private _toastrService = inject(ToastrService);

    public offersStore = inject(OffersStore);

    public form = new FormGroup({
        id: new FormControl<number>(0, { nonNullable: true }),
        name: new FormControl<string>("", { nonNullable: true, validators: [Validators.required] }),
        startDate: new FormControl<Nullable<DateTime>>(null),
        startDateHours: new FormControl<Nullable<number>>(null),
        startDateMinutes: new FormControl<number | null>(null),
        endDate: new FormControl<Nullable<DateTime>>(null),
        endDateHours: new FormControl<Nullable<number>>(null),
        endDateMinutes: new FormControl<Nullable<number>>(null),
        isActive: new FormControl<boolean>(false, { nonNullable: true }),
        installedOffersIgnoreList: new FormControl<Nullable<number[]>>([]),
        groups: new FormArray<FormGroup<AbTestGroupForm>>([]),
    });

    constructor() {
        effect(() => this.groupLoadedEffectFn());
    }

    public addGroup(group?: AbTestGroupViewModel): void {
        this.form.controls.groups.push(
            new FormGroup({
                id: new FormControl<number>(group?.id ?? 0, { nonNullable: true }),
                name: new FormControl<string>(group?.name ?? "", {
                    nonNullable: true,
                    validators: [Validators.required],
                }),
                targetUsersPercentage: new FormControl<number>(group?.targetUsersPercentage ?? 0, {
                    nonNullable: true,
                }),
                usersCount: new FormControl<number>(
                    { value: group?.usersCount ?? 0, disabled: true },
                    { nonNullable: true },
                ),
            }),
        );
    }

    public createOrUpdateTest(): void {
        const formValue = this.form.getRawValue();

        const model: AbTestEditModel = {
            id: formValue.id,
            name: formValue.name,
            startDate: formValue.startDate
                ?.plus({
                    hours: formValue.startDateHours ?? 0,
                    minutes: formValue.startDateMinutes ?? 0,
                })
                .toISO(),
            endDate: formValue.endDate
                ?.plus({
                    hours: formValue.endDateHours ?? 0,
                    minutes: formValue.endDateMinutes ?? 0,
                })
                .toISO(),
            isActive: formValue.isActive,
            versions: [],
            installedOffersIgnoreList: formValue.installedOffersIgnoreList,
            groups: formValue.groups,
        };

        let request: Observable<AbTestViewModel>;

        if (model.id) {
            request = this.abTestStore.updateTest(model);
        } else {
            request = this.abTestsStore.createTest(model);
        }

        request.subscribe({
            next: () => this._toastrService.success("Success"),
            error: (error) => {
                this._toastrService.error(error.error.description ?? "Internal Error");
            },
        });
    }

    private groupLoadedEffectFn(): void {
        const abTest = this.abTestStore.abTest();

        if (abTest) {
            this.form.patchValue({
                id: abTest.id,
                name: abTest.name,
                startDate: abTest.startDate
                    ? DateTime.fromISO(abTest.startDate, { zone: "UTC" })
                    : null,
                endDate: abTest.endDate ? DateTime.fromISO(abTest.endDate, { zone: "UTC" }) : null,
                installedOffersIgnoreList: abTest.installedOffersIgnoreList,
                isActive: abTest.isActive,
            });

            if (!this.form.controls.groups.length) {
                for (const group of abTest.groups) {
                    this.addGroup(group);
                }
            }
        }
    }
}
